// Iter 30p — Anagrafica Clienti con ricerca AI online (Gemini)
// Solo admin. Workflow:
//   1. Nuovo cliente → utente inserisce almeno il nome
//   2. Clic "🔍 Cerca dati online" → AI propone ragione sociale, P.IVA, indirizzo...
//   3. Utente conferma o modifica → salva
//   4. Backend de-duplica per partita_iva o nome normalizzato
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Pencil, Trash2, Sparkles, Loader2, Users, Globe, MapPin, Building2,
  ShieldAlert, Download, AlertTriangle, Link2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import DuplicatiClientiBanner from "@/components/DuplicatiClientiBanner";

const emptyCliente = {
  nome: "",
  partita_iva: "",
  codice_fiscale: "",
  indirizzo: "",
  citta: "",
  cap: "",
  provincia: "",
  telefono: "",
  email: "",
  sito_web: "",
  settore: "",
  note: "",
  // Iter 31v/31w: codice primario legacy (singolo) + lista stabilimenti.
  // Un cliente può avere N stabilimenti, ognuno col proprio codice EG-XX
  // (es. Bormioli Luigi: {90,"Parma"}, {91,"Abbiategrasso"}). Il codice
  // 99 è jolly. La pagina lavora prevalentemente con `stabilimenti`.
  codice_primario: "",
  stabilimenti: [],
};

export default function ClientiPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyCliente);
  const [editingId, setEditingId] = useState(null);
  const [researching, setResearching] = useState(false);
  const [proposedHint, setProposedHint] = useState(null); // {duplicate?}
  const [migrating, setMigrating] = useState(false);
  // Iter 30t: ultime 3 commesse del cliente, mostrate nell'edit dialog
  const [recentCommesse, setRecentCommesse] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/clienti");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      // Iter 30p hot-fix: errore visibile invece di array vuoto silente
      const status = e?.response?.status;
      // eslint-disable-next-line no-console
      console.error("[Clienti] load failed:", status, e?.message);
      if (status === 401 || status === 403) {
        toast.error("Sessione scaduta. Fai logout e login di nuovo.");
      } else {
        toast.error(
          `Caricamento clienti non riuscito${status ? ` (HTTP ${status})` : ""}`
        );
      }
      setItems([]);
    }
  };

  useEffect(() => { load(); }, []);

  // Iter 31s: deep-link da Dashboard → auto-attiva il filtro "Solo senza P.IVA"
  // e mostra un banner suggerendo la ricerca AI sui clienti incompleti.
  const [aiResearchHint, setAiResearchHint] = useState(false);
  // Iter 31t: wizard "Sito-first". Quando l'API website ritorna un URL,
  // lo salviamo qui e mostriamo il banner di conferma con i due bottoni
  // "Sì, usa questo sito" / "No, cerca altrove".
  const [siteCandidate, setSiteCandidate] = useState(null);
  const [busySiteUrl, setBusySiteUrl] = useState("");
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("ai-research") === "1") {
      setOnlyMissingPiva(true);
      setAiResearchHint(true);
      // Pulisci il query param senza ricaricare la pagina
      window.history.replaceState(
        {},
        "",
        window.location.pathname
      );
    }
  }, []);

  // Iter 30s/30t: filtro "Solo senza P.IVA" — accetta anche formati esteri
  // (alfanumerici), basta che ci sia almeno qualcosa di sensato.
  const _hasValidPiva = (c) => {
    const norm = (c.partita_iva || "")
      .replace(/[^a-zA-Z0-9]/g, "")
      .toUpperCase();
    return norm.length >= 5;
  };
  const [onlyMissingPiva, setOnlyMissingPiva] = useState(false);
  const missingPivaCount = useMemo(
    () => items.filter((c) => !_hasValidPiva(c)).length,
    [items]
  );

  // Iter 30w: alert "clienti orfani" (senza commesse) all'ingresso della pagina
  const [orfani, setOrfani] = useState([]);
  const [orfaniOpen, setOrfaniOpen] = useState(false);
  const [orfaniSelected, setOrfaniSelected] = useState(new Set());
  const [orfaniBusy, setOrfaniBusy] = useState(false);

  // Iter 31v: codici primari "EG-XX" trovati nelle commesse ma non assegnati
  // a nessun cliente. L'admin li assegna manualmente da banner persistente.
  // Iter 31w: ora ogni assegnazione aggiunge uno STABILIMENTO al cliente
  // (nome stabilimento opzionale).
  // Iter 31bb: ricerca AI inline — crea nuovo cliente direttamente dal banner
  // (cerca per nome o P.IVA, preview campi, conferma → crea + assegna codice).
  const [codiciOrfani, setCodiciOrfani] = useState([]);
  const [codiciAssignTarget, setCodiciAssignTarget] = useState({}); // {codice: cliente_id}
  const [codiciAssignNome, setCodiciAssignNome] = useState({}); // {codice: stabilimento_nome}
  const [codiciBusy, setCodiciBusy] = useState({}); // {codice: bool}
  // Stato ricerca AI per ogni codice orfano
  const [aiPanelOpen, setAiPanelOpen] = useState({}); // {codice: bool}
  const [aiSearchType, setAiSearchType] = useState({}); // {codice: "nome"|"piva"}
  const [aiSearchQuery, setAiSearchQuery] = useState({}); // {codice: str}
  const [aiSearchResult, setAiSearchResult] = useState({}); // {codice: {nome, partita_iva, ...}}
  const [aiSearchBusy, setAiSearchBusy] = useState({}); // {codice: bool}

  const loadCodiciOrfani = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/clienti/codici-orfani");
      setCodiciOrfani(Array.isArray(data) ? data : []);
    } catch (_) {
      setCodiciOrfani([]);
    }
  };

  useEffect(() => {
    loadCodiciOrfani();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const assignCodice = async (codice) => {
    const targetId = codiciAssignTarget[codice];
    if (!targetId) {
      toast.error("Seleziona prima un cliente");
      return;
    }
    const stabilimentoNome = (codiciAssignNome[codice] || "").trim();
    setCodiciBusy((b) => ({ ...b, [codice]: true }));
    try {
      const { data } = await api.post(`/clienti/${targetId}/assign-codice`, {
        codice,
        stabilimento_nome: stabilimentoNome,
      });
      toast.success(
        `Codice ${codice}${stabilimentoNome ? ` (${stabilimentoNome})` : ""} assegnato. ${data.commesse_rebound || 0} commesse aggiornate.`
      );
      await Promise.all([load(), loadCodiciOrfani()]);
      setCodiciAssignTarget((t) => {
        const n = { ...t };
        delete n[codice];
        return n;
      });
      setCodiciAssignNome((t) => {
        const n = { ...t };
        delete n[codice];
        return n;
      });
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || "Assegnazione fallita";
      toast.error(msg);
    } finally {
      setCodiciBusy((b) => ({ ...b, [codice]: false }));
    }
  };

  // Iter 31bb: ricerca AI per creare nuovo cliente direttamente dal banner.
  // L'utente cerca per nome azienda o P.IVA, l'AI propone i campi, l'utente
  // conferma → POST /clienti + POST /assign-codice in chain.
  const openAiPanel = (codice) => {
    setAiPanelOpen((p) => ({ ...p, [codice]: true }));
    if (!aiSearchType[codice])
      setAiSearchType((t) => ({ ...t, [codice]: "nome" }));
  };
  const closeAiPanel = (codice) => {
    setAiPanelOpen((p) => ({ ...p, [codice]: false }));
    setAiSearchResult((r) => {
      const n = { ...r };
      delete n[codice];
      return n;
    });
  };
  const runAiSearch = async (codice) => {
    const type = aiSearchType[codice] || "nome";
    const q = (aiSearchQuery[codice] || "").trim();
    if (!q) {
      toast.error(
        type === "piva"
          ? "Inserisci la P.IVA"
          : "Inserisci il nome dell'azienda"
      );
      return;
    }
    setAiSearchBusy((b) => ({ ...b, [codice]: true }));
    try {
      // L'endpoint /clienti/research accetta `nome` come query libera + hint
      // opzionale. Se l'utente cerca per P.IVA, la passiamo nel campo `nome`
      // anteponendo "P.IVA " per aiutare l'LLM a interpretarla correttamente.
      const queryText = type === "piva" ? `P.IVA ${q}` : q;
      const { data } = await api.post(
        "/clienti/research",
        { nome: queryText, hint: "" },
        { timeout: 45000 }
      );
      const proposed = data?.proposed || {};
      if (data?.duplicate) {
        toast.warning(
          `Cliente già in anagrafica: "${data.duplicate.nome}". Usa il dropdown.`
        );
        // Pre-seleziona il duplicate nel dropdown e chiudi il pannello
        setCodiciAssignTarget((t) => ({
          ...t,
          [codice]: data.duplicate.id,
        }));
        closeAiPanel(codice);
        return;
      }
      setAiSearchResult((r) => ({
        ...r,
        [codice]: {
          nome: proposed.nome || q,
          partita_iva: proposed.partita_iva || (type === "piva" ? q : ""),
          codice_fiscale: proposed.codice_fiscale || "",
          indirizzo: proposed.indirizzo || "",
          citta: proposed.citta || "",
          cap: proposed.cap || "",
          provincia: proposed.provincia || "",
          settore: proposed.settore || "",
          sito_web: proposed.sito_web || "",
          telefono: proposed.telefono || "",
          email: proposed.email || "",
        },
      }));
      toast.success("AI ha trovato i dati. Controlla e conferma.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Ricerca AI non riuscita";
      toast.error(typeof msg === "string" ? msg : "Ricerca AI non riuscita");
    } finally {
      setAiSearchBusy((b) => ({ ...b, [codice]: false }));
    }
  };
  const createClienteAndAssign = async (codice) => {
    const result = aiSearchResult[codice];
    if (!result || !result.nome?.trim()) {
      toast.error("Nome cliente obbligatorio");
      return;
    }
    setAiSearchBusy((b) => ({ ...b, [codice]: true }));
    try {
      // 1. Crea cliente nuovo. Includo già lo stabilimento col codice corrente.
      const stabNome = (codiciAssignNome[codice] || "").trim();
      const payload = {
        ...result,
        note: "",
        stabilimenti: [{ codice, nome: stabNome }],
      };
      const { data: nuovo } = await api.post("/clienti", payload);
      toast.success(
        `Cliente "${nuovo.nome}" creato e codice ${codice} assegnato (${nuovo.commesse_rebound || 0} commesse aggiornate)`,
        { duration: 6000 }
      );
      closeAiPanel(codice);
      setAiSearchQuery((q) => {
        const n = { ...q };
        delete n[codice];
        return n;
      });
      setCodiciAssignNome((t) => {
        const n = { ...t };
        delete n[codice];
        return n;
      });
      await Promise.all([load(), loadCodiciOrfani()]);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg =
        (typeof detail === "object" ? detail.message : detail) ||
        "Creazione cliente non riuscita";
      toast.error(msg);
    } finally {
      setAiSearchBusy((b) => ({ ...b, [codice]: false }));
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    const flag = "clienti_orfani_alert_shown_v1";
    if (sessionStorage.getItem(flag)) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/clienti-orfani");
        const arr = Array.isArray(data) ? data : [];
        if (arr.length > 0) {
          setOrfani(arr);
          setOrfaniSelected(new Set());
          setOrfaniOpen(true);
        }
        sessionStorage.setItem(flag, "1");
      } catch (err) {
        console.warn("[clienti] check clienti orfani fallito:", err?.message);
      }
    })();
  }, [isAdmin]);

  const toggleOrfano = (id) => {
    setOrfaniSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const trashOrfani = async () => {
    if (orfaniSelected.size === 0) {
      setOrfaniOpen(false);
      return;
    }
    setOrfaniBusy(true);
    try {
      const { data } = await api.post("/admin/clienti-bulk-delete", {
        ids: Array.from(orfaniSelected),
      });
      toast.success(
        `Spostati nel cestino ${data?.trashed || 0} clienti senza commesse.`
      );
      setOrfaniOpen(false);
      load();
    } catch (_) {
      toast.error("Eliminazione clienti non riuscita");
    } finally {
      setOrfaniBusy(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let res = items;
    if (onlyMissingPiva) {
      res = res.filter((c) => !_hasValidPiva(c));
    }
    if (!q) return res;
    return res.filter(
      (c) =>
        (c.nome || "").toLowerCase().includes(q) ||
        (c.partita_iva || "").toLowerCase().includes(q) ||
        (c.citta || "").toLowerCase().includes(q) ||
        (c.settore || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const startCreate = () => {
    setForm(emptyCliente);
    setEditingId(null);
    setProposedHint(null);
    setRecentCommesse([]);
    setOpen(true);
  };

  const startEdit = async (it) => {
    setForm({ ...emptyCliente, ...it });
    setEditingId(it.id);
    setProposedHint(null);
    setRecentCommesse([]);
    setOpen(true);
    // Iter 30t: carica le ultime 3 commesse del cliente
    try {
      const { data } = await api.get(`/clienti/${it.id}/commesse-recenti?limit=3`);
      setRecentCommesse(Array.isArray(data) ? data : []);
    } catch (_) {
      setRecentCommesse([]);
    }
  };

  // Iter 30s: avvia EDIT + lancia subito ricerca AI (per completare P.IVA mancante)
  const startEditAndResearch = async (it) => {
    setForm({ ...emptyCliente, ...it });
    setEditingId(it.id);
    setProposedHint(null);
    setOpen(true);
    // Lascia che il dialog faccia il mount, poi triggera la research
    await new Promise((r) => setTimeout(r, 200));
    research();
  };

  const research = async () => {
    const nome = (form.nome || "").trim();
    if (!nome) {
      toast.error("Inserisci almeno il nome dell'azienda");
      return;
    }
    setResearching(true);
    setProposedHint(null);
    // Iter 31t — wizard "Sito-first": prima cerca il sito ufficiale e
    // chiedi conferma; se confermato, estrai dati dal sito; altrimenti
    // fallback alla ricerca generica come prima.
    try {
      const { data: site } = await api.post(
        "/clienti/research/website",
        { nome, hint: form.citta || "" },
        { timeout: 35000 }
      );
      const cands = site?.candidates || [];
      if (cands.length > 0) {
        setSiteCandidate({
          nome,
          candidates: cands,
        });
        setResearching(false);
        return; // attende conferma utente nel banner
      }
      // Nessun sito trovato → fallback ricerca generica
      await researchGeneric(nome);
    } catch (e) {
      // Errore sul website lookup → fallback generico
      await researchGeneric(nome);
    } finally {
      setResearching(false);
    }
  };

  // Step 2: dopo conferma del sito ufficiale, estrai dati dal sito.
  // Accetta opzionalmente il candidato selezionato (per supportare la scelta
  // tra più candidati).
  const researchFromSite = async (selected = null) => {
    const chosen = selected || (siteCandidate?.candidates || [])[0];
    if (!siteCandidate || !chosen) return;
    setBusySiteUrl(chosen.sito_web || "");
    setResearching(true);
    try {
      const { data } = await api.post(
        "/clienti/research/from-website",
        {
          nome: siteCandidate.nome,
          sito_web: chosen.sito_web,
          exclude_id: editingId || undefined,
        },
        { timeout: 45000 }
      );
      _applyProposed(data?.proposed || {}, data?.duplicate);
      setSiteCandidate(null);
    } catch (e) {
      toast.error("Estrazione dal sito non riuscita, provo ricerca generica…");
      await researchGeneric(siteCandidate.nome);
      setSiteCandidate(null);
    } finally {
      setResearching(false);
    }
  };

  // Fallback: vecchia ricerca generica
  const researchGeneric = async (nome) => {
    try {
      const { data } = await api.post(
        "/clienti/research",
        { nome, hint: form.citta || "", piva: form.partita_iva || "", exclude_id: editingId || undefined },
        { timeout: 45000 }
      );
      _applyProposed(data?.proposed || {}, data?.duplicate);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Ricerca AI non riuscita";
      toast.error(typeof msg === "string" ? msg : "Ricerca AI non riuscita");
    }
  };

  const _applyProposed = (p, duplicate) => {
    setForm((prev) => ({
      ...prev,
      nome: (prev.nome || "").trim() || p.nome || prev.nome,
      partita_iva: prev.partita_iva || p.partita_iva || "",
      codice_fiscale: prev.codice_fiscale || p.codice_fiscale || "",
      indirizzo: prev.indirizzo || p.indirizzo || "",
      citta: prev.citta || p.citta || "",
      cap: prev.cap || p.cap || "",
      provincia: prev.provincia || p.provincia || "",
      telefono: prev.telefono || p.telefono || "",
      email: prev.email || p.email || "",
      sito_web: prev.sito_web || p.sito_web || "",
      settore: prev.settore || p.settore || "",
      note: prev.note || p.note || "",
    }));
    if (duplicate) {
      setProposedHint({ duplicate });
      toast.warning(
        `Attenzione: già presente "${duplicate.nome}" con dati simili`
      );
    } else {
      const filledCount = Object.values(p).filter(
        (v) => v && String(v).trim() !== ""
      ).length;
      toast.success(`AI ha pre-compilato ${filledCount} campi.`);
    }
  };

  // legacy: rimuovo il vecchio blocco try/catch della research
  const _legacyResearch = async () => {
    try {
      const { data } = await api.post(
        "/clienti/research",
        { nome: form.nome, hint: form.citta || "", exclude_id: editingId || undefined },
        { timeout: 45000 }
      );
      _applyProposed(data?.proposed || {}, data?.duplicate);
    } catch (err) {
      console.warn("[clienti] ricerca AI fallita:", err?.message);
    }
  };

  const save = async () => {
    const nome = (form.nome || "").trim();
    if (!nome) {
      toast.error("Il nome del cliente è obbligatorio");
      return;
    }
    // Iter 30s/30t: P.IVA obbligatoria. Per clienti esteri accettiamo qualsiasi
    // formato alfanumerico (DE123456789, GB12345, ABN australiano, ecc.).
    const pivaNorm = (form.partita_iva || "")
      .replace(/[^a-zA-Z0-9]/g, "")
      .toUpperCase();
    if (!pivaNorm || pivaNorm.length < 5) {
      toast.error(
        "P.IVA obbligatoria (italiana o estera). Clicca 'Cerca dati online' " +
          "o modifica il nome e riprova; in alternativa inseriscila a mano " +
          "(min. 5 caratteri)."
      );
      return;
    }
    // Iter 46: almeno uno stabilimento con codice è obbligatorio
    const stabValidi = (form.stabilimenti || []).filter((s) =>
      (s.codice || "").trim()
    );
    if (stabValidi.length === 0) {
      toast.error(
        "Stabilimento obbligatorio: aggiungi almeno un codice stabilimento a 2 cifre (EG-XX)."
      );
      return;
    }
    try {
      if (editingId) {
        await api.put(`/clienti/${editingId}`, form);
        toast.success("Cliente aggiornato");
      } else {
        await api.post("/clienti", form);
        toast.success("Cliente salvato");
      }
      setOpen(false);
      load();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && detail?.duplicate) {
        toast.error(
          `Cliente già esistente: "${detail.duplicate.nome}" (P.IVA: ${
            detail.duplicate.partita_iva || "n/d"
          })`
        );
      } else {
        toast.error(typeof detail === "string" ? detail : "Salvataggio non riuscito");
      }
    }
  };

  const remove = async (id, nome) => {
    if (!window.confirm(`Spostare "${nome}" nel cestino?`)) return;
    try {
      await api.delete(`/clienti/${id}`);
      toast.success("Cliente spostato nel cestino");
      load();
    } catch (e) {
      toast.error("Eliminazione non riuscita");
    }
  };

  const migrateFromCommesse = async () => {
    if (
      !window.confirm(
        "Crea automaticamente i clienti a partire dai nomi unici già presenti " +
        "nelle commesse esistenti? L'operazione è sicura e non duplica clienti."
      )
    ) return;
    setMigrating(true);
    try {
      const { data } = await api.post("/clienti/migrate-from-commesse");
      toast.success(
        `Importati ${data?.created || 0} nuovi clienti (${data?.skipped || 0} già esistenti).`
      );
      load();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      // Iter 30p hot-fix: messaggio diagnostico al posto del generico
      let msg = "Migrazione non riuscita";
      if (status === 401 || status === 403) {
        msg = "Sessione scaduta. Fai logout e login di nuovo.";
      } else if (status) {
        msg = `Errore ${status}: ${typeof detail === "string" ? detail : "riprova"}`;
      } else if (e?.message) {
        msg = `Errore di rete: ${e.message}. Verifica connessione e riprova.`;
      }
      toast.error(msg);
      // eslint-disable-next-line no-console
      console.error("[Clienti] migrate failed:", e);
    } finally {
      setMigrating(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-12 text-center">
        <ShieldAlert className="h-12 w-12 mx-auto text-slate-300" />
        <div className="font-heading font-bold text-xl mt-3">
          Accesso riservato agli admin
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
            Anagrafica
          </div>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">
            Clienti
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {items.length} clienti registrati. Ricerca AI online disponibile per
            popolare automaticamente partita IVA, indirizzo e altri dati.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={migrateFromCommesse}
            disabled={migrating}
            data-testid="migrate-clienti-button"
            className="h-12"
          >
            {migrating ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Importa da commesse
          </Button>
          <Button
            onClick={startCreate}
            data-testid="add-cliente-button"
            className="h-12 bg-[#0044FF] hover:bg-[#0033CC]"
          >
            <Plus className="h-5 w-5 mr-2" /> Nuovo cliente
          </Button>
        </div>
      </div>

      <Card className="p-4 space-y-3">
        <Input
          placeholder="Cerca cliente per nome, P.IVA, città o settore…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-12"
          data-testid="search-clienti-input"
        />
        {missingPivaCount > 0 && (
          <button
            type="button"
            onClick={() => setOnlyMissingPiva((v) => !v)}
            data-testid="toggle-missing-piva"
            className={`w-full text-left px-3 py-2 rounded-md border-2 text-sm transition-colors ${
              onlyMissingPiva
                ? "border-amber-500 bg-amber-50 text-amber-900"
                : "border-slate-200 bg-white hover:border-amber-300"
            }`}
          >
            <span className="font-bold">⚠️ {missingPivaCount} clienti senza P.IVA</span>
            <span className="text-slate-500 ml-2">
              {onlyMissingPiva
                ? "(filtro attivo, clicca per disattivare)"
                : "— clicca per filtrare e completare"}
            </span>
          </button>
        )}
      </Card>

      {aiResearchHint && (
        <Card
          className="p-4 bg-purple-50 border-purple-200"
          data-testid="ai-research-hint-banner"
        >
          <div className="flex items-start gap-2.5">
            <span className="text-xl shrink-0">✨</span>
            <div className="flex-1 text-sm text-purple-900">
              <strong className="block">Arrivi dalla dashboard?</strong>
              Ho filtrato i <strong>clienti senza P.IVA</strong>. Per ognuno
              clicca <strong>&quot;Modifica&quot;</strong> e poi il pulsante{" "}
              <strong>&quot;Cerca con AI&quot;</strong> nel form — l&apos;app
              consulta <code>ufficiocamerale.it</code> e ti pre-compila
              P.IVA, indirizzo, città e settore.
            </div>
            <button
              type="button"
              className="text-xs underline text-purple-700 hover:text-purple-900"
              onClick={() => setAiResearchHint(false)}
              data-testid="ai-research-hint-dismiss"
            >
              Chiudi
            </button>
          </div>
        </Card>
      )}

      {/* Iter 31ff: banner doppioni clienti — l'admin li collassa nel principale */}
      {isAdmin && <DuplicatiClientiBanner onMerged={load} />}

      {/* Iter 31v: banner codici primari orfani - admin assegna manualmente */}
      {isAdmin && codiciOrfani.length > 0 && (
        <Card
          className="p-4 bg-blue-50 border-blue-300 border-2"
          data-testid="codici-orfani-banner"
        >
          <div className="flex items-start gap-2 mb-3">
            <Link2 className="h-5 w-5 text-blue-700 shrink-0 mt-0.5" />
            <div className="flex-1 text-sm text-blue-900">
              <strong className="block text-base">
                {codiciOrfani.length} codice
                {codiciOrfani.length === 1 ? "" : "i"} commessa senza cliente
              </strong>
              Ci sono commesse <code className="font-mono font-bold">EG-XX-…</code>{" "}
              il cui codice <strong>XX</strong> non è stato ancora assegnato a un
              cliente. Per ognuno seleziona il cliente corretto e clicca{" "}
              <strong>&quot;Assegna&quot;</strong>: tutte le commesse della
              famiglia EG-XX-… verranno automaticamente collegate. Il codice{" "}
              <strong>99</strong> è jolly e non appare qui.
            </div>
          </div>
          <div className="space-y-2">
            {codiciOrfani.map((co) => {
              const examples = co.esempi_codici.join(", ");
              const currentClient = co.clienti_attuali[0] || "";
              // Pre-seleziona il cliente attuale se esiste e c'è match per nome
              const suggested = items.find(
                (c) =>
                  (c.nome || "").trim().toLowerCase() ===
                  (currentClient || "").trim().toLowerCase()
              );
              return (
                <div
                  key={co.codice}
                  className="space-y-2"
                  data-testid={`codice-orfano-${co.codice}`}
                >
                  <div className="bg-white rounded-md border border-blue-200 p-2.5 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center rounded-md bg-blue-600 text-white px-2 py-1 font-mono font-bold text-sm shrink-0">
                    EG-{co.codice}
                  </span>
                  <div className="flex-1 min-w-[180px] text-xs text-slate-700">
                    <div>
                      <strong>{co.n_commesse}</strong> commess
                      {co.n_commesse === 1 ? "a" : "e"}
                      {currentClient && (
                        <>
                          {" · "}
                          attuale: <em>{currentClient}</em>
                        </>
                      )}
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono truncate">
                      {examples}
                    </div>
                  </div>
                  <select
                    className="h-9 rounded-md border border-slate-300 px-2 text-sm bg-white min-w-[200px]"
                    value={
                      codiciAssignTarget[co.codice] ?? (suggested?.id || "")
                    }
                    onChange={(e) =>
                      setCodiciAssignTarget((t) => ({
                        ...t,
                        [co.codice]: e.target.value,
                      }))
                    }
                    data-testid={`codice-orfano-select-${co.codice}`}
                  >
                    <option value="">— Seleziona cliente —</option>
                    {items.map((c) => {
                      const stabs =
                        (c.stabilimenti && c.stabilimenti.length > 0)
                          ? c.stabilimenti
                          : c.codice_primario
                            ? [{ codice: c.codice_primario, nome: "" }]
                            : [];
                      const stabsLabel = stabs
                        .map((s) =>
                          s.nome ? `EG-${s.codice} ${s.nome}` : `EG-${s.codice}`
                        )
                        .join(", ");
                      return (
                        <option key={c.id} value={c.id}>
                          {c.nome}
                          {stabsLabel ? ` (${stabsLabel})` : ""}
                        </option>
                      );
                    })}
                  </select>
                  <Input
                    value={codiciAssignNome[co.codice] || ""}
                    onChange={(e) =>
                      setCodiciAssignNome((t) => ({
                        ...t,
                        [co.codice]: e.target.value,
                      }))
                    }
                    placeholder="Stabilimento (es. Parma) — opzionale"
                    className="h-9 w-52"
                    data-testid={`codice-orfano-stabilimento-${co.codice}`}
                  />
                  <Button
                    size="sm"
                    onClick={() => assignCodice(co.codice)}
                    disabled={
                      codiciBusy[co.codice] ||
                      !(
                        codiciAssignTarget[co.codice] ?? suggested?.id
                      )
                    }
                    className="h-9 bg-blue-600 hover:bg-blue-700"
                    data-testid={`codice-orfano-assign-${co.codice}`}
                  >
                    {codiciBusy[co.codice] ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Assegna"
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      aiPanelOpen[co.codice]
                        ? closeAiPanel(co.codice)
                        : openAiPanel(co.codice)
                    }
                    className="h-9 border-purple-300 text-purple-700 hover:bg-purple-50"
                    data-testid={`codice-orfano-ai-toggle-${co.codice}`}
                    title="Crea nuovo cliente con ricerca AI"
                  >
                    <Sparkles className="h-4 w-4 mr-1" />
                    Cerca AI
                  </Button>
                </div>
                {/* Pannello AI inline */}
                {aiPanelOpen[co.codice] && (
                  <div
                    className="mt-2 w-full bg-purple-50 border border-purple-200 rounded-md p-3 space-y-2"
                    data-testid={`codice-orfano-ai-panel-${co.codice}`}
                  >
                    <div className="text-xs font-bold text-purple-900 flex items-center gap-1.5">
                      <Sparkles className="h-3.5 w-3.5" />
                      Crea nuovo cliente con AI e assegna codice EG-{co.codice}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="inline-flex rounded-md border border-purple-300 overflow-hidden">
                        <button
                          type="button"
                          onClick={() =>
                            setAiSearchType((t) => ({
                              ...t,
                              [co.codice]: "nome",
                            }))
                          }
                          className={`px-3 py-1.5 text-xs font-bold ${
                            (aiSearchType[co.codice] || "nome") === "nome"
                              ? "bg-purple-600 text-white"
                              : "bg-white text-purple-700"
                          }`}
                          data-testid={`ai-type-nome-${co.codice}`}
                        >
                          Per nome
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setAiSearchType((t) => ({
                              ...t,
                              [co.codice]: "piva",
                            }))
                          }
                          className={`px-3 py-1.5 text-xs font-bold ${
                            aiSearchType[co.codice] === "piva"
                              ? "bg-purple-600 text-white"
                              : "bg-white text-purple-700"
                          }`}
                          data-testid={`ai-type-piva-${co.codice}`}
                        >
                          Per P.IVA
                        </button>
                      </div>
                      <Input
                        value={aiSearchQuery[co.codice] || ""}
                        onChange={(e) =>
                          setAiSearchQuery((q) => ({
                            ...q,
                            [co.codice]: e.target.value,
                          }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            runAiSearch(co.codice);
                          }
                        }}
                        placeholder={
                          aiSearchType[co.codice] === "piva"
                            ? "es. IT12345678901"
                            : "es. Bormioli Luigi SpA"
                        }
                        className="h-9 flex-1 min-w-[180px] bg-white"
                        data-testid={`ai-query-${co.codice}`}
                        autoFocus
                      />
                      <Button
                        size="sm"
                        onClick={() => runAiSearch(co.codice)}
                        disabled={
                          aiSearchBusy[co.codice] ||
                          !(aiSearchQuery[co.codice] || "").trim()
                        }
                        className="h-9 bg-purple-600 hover:bg-purple-700"
                        data-testid={`ai-search-${co.codice}`}
                      >
                        {aiSearchBusy[co.codice] ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          "Cerca con AI"
                        )}
                      </Button>
                    </div>
                    {/* Preview dei dati trovati, editabili */}
                    {aiSearchResult[co.codice] && (
                      <div className="bg-white rounded-md border border-purple-200 p-2.5 space-y-1.5">
                        <div className="text-[11px] font-bold text-purple-800 mb-1">
                          ✨ Dati trovati — controlla e correggi se serve
                        </div>
                        {[
                          { k: "nome", label: "Ragione sociale *", req: true },
                          { k: "partita_iva", label: "P.IVA" },
                          { k: "indirizzo", label: "Indirizzo" },
                          { k: "citta", label: "Città" },
                          { k: "cap", label: "CAP" },
                          { k: "provincia", label: "Prov." },
                          { k: "settore", label: "Settore" },
                          { k: "sito_web", label: "Sito web" },
                        ].map((f) => (
                          <div
                            key={f.k}
                            className="grid grid-cols-3 gap-2 items-center"
                          >
                            <label className="text-[11px] text-slate-700 font-bold col-span-1">
                              {f.label}
                            </label>
                            <Input
                              value={aiSearchResult[co.codice][f.k] || ""}
                              onChange={(e) =>
                                setAiSearchResult((r) => ({
                                  ...r,
                                  [co.codice]: {
                                    ...r[co.codice],
                                    [f.k]: e.target.value,
                                  },
                                }))
                              }
                              className="h-8 text-xs col-span-2"
                              data-testid={`ai-field-${co.codice}-${f.k}`}
                            />
                          </div>
                        ))}
                        <div className="grid grid-cols-3 gap-2 items-center pt-1">
                          <label className="text-[11px] text-slate-700 font-bold col-span-1">
                            Stabilimento
                          </label>
                          <Input
                            value={codiciAssignNome[co.codice] || ""}
                            onChange={(e) =>
                              setCodiciAssignNome((t) => ({
                                ...t,
                                [co.codice]: e.target.value,
                              }))
                            }
                            placeholder="es. Parma — opzionale"
                            className="h-8 text-xs col-span-2"
                            data-testid={`ai-stab-${co.codice}`}
                          />
                        </div>
                        <div className="flex gap-2 pt-2">
                          <Button
                            size="sm"
                            onClick={() => createClienteAndAssign(co.codice)}
                            disabled={
                              aiSearchBusy[co.codice] ||
                              !(aiSearchResult[co.codice].nome || "").trim()
                            }
                            className="h-8 bg-emerald-600 hover:bg-emerald-700 text-xs"
                            data-testid={`ai-confirm-${co.codice}`}
                          >
                            {aiSearchBusy[co.codice] ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <>✓ Crea cliente e assegna codice</>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => closeAiPanel(co.codice)}
                            className="h-8 text-xs"
                            data-testid={`ai-cancel-${co.codice}`}
                          >
                            Annulla
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {filtered.length === 0 ? (
        <Card className="p-10 text-center">
          <Users className="h-10 w-10 mx-auto text-slate-300" />
          <div className="font-heading font-bold text-lg mt-3">
            Nessun cliente
          </div>
          <p className="text-sm text-slate-500">
            {items.length === 0
              ? "Crea il primo cliente o importali dalle commesse."
              : "Nessun cliente corrisponde al filtro."}
          </p>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((it) => (
            <Card
              key={it.id}
              className="p-4 flex items-start gap-3"
              data-testid={`cliente-row-${it.id}`}
            >
              <div className="hidden sm:flex h-10 w-10 rounded-md bg-slate-900 text-white items-center justify-center shrink-0">
                <Building2 className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold truncate flex flex-wrap items-center gap-1.5">
                  {(() => {
                    // Iter 31w: mostra TUTTI gli stabilimenti come badge.
                    // Compat: se manca `stabilimenti` ma c'è `codice_primario`
                    // mostriamo un badge singolo legacy.
                    const stabs =
                      (it.stabilimenti && it.stabilimenti.length > 0)
                        ? it.stabilimenti
                        : it.codice_primario
                          ? [{ codice: it.codice_primario, nome: "" }]
                          : [];
                    return stabs.map((st, i) => (
                      <span
                        key={`${st.codice}-${i}`}
                        className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-bold font-mono shrink-0 ${
                          st.codice === "99"
                            ? "bg-amber-100 text-amber-800 border border-amber-300"
                            : "bg-blue-100 text-blue-800 border border-blue-300"
                        }`}
                        data-testid={`cliente-codice-badge-${it.id}-${st.codice}`}
                        title={
                          st.codice === "99"
                            ? `Codice 99 — jolly${st.nome ? ` · ${st.nome}` : ""}`
                            : `Codice ${st.codice}${st.nome ? ` · stabilimento ${st.nome}` : ""} — commesse EG-${st.codice}-…`
                        }
                      >
                        EG-{st.codice}
                        {st.nome && (
                          <span className="ml-1 font-normal opacity-80">
                            {st.nome}
                          </span>
                        )}
                      </span>
                    ));
                  })()}
                  <span className="truncate">{it.nome}</span>
                </div>
                <div className="text-xs text-slate-600 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                  {it.partita_iva && (
                    <span className="font-mono">
                      P.IVA <span className="font-bold">{it.partita_iva}</span>
                    </span>
                  )}
                  {(it.citta || it.provincia) && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {[it.citta, it.provincia].filter(Boolean).join(" ")}
                    </span>
                  )}
                  {it.sito_web && (
                    <a
                      href={it.sito_web}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                    >
                      <Globe className="h-3 w-3" /> sito
                    </a>
                  )}
                </div>
                {it.settore && (
                  <div className="text-[11px] text-slate-500 mt-1 truncate">
                    {it.settore}
                  </div>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  size="icon"
                  variant="outline"
                  className="h-10 w-10"
                  onClick={() => startEdit(it)}
                  data-testid={`edit-cliente-${it.id}`}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  className="h-10 w-10 text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => remove(it.id, it.nome)}
                  data-testid={`delete-cliente-${it.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog di creazione / modifica */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Modifica cliente" : "Nuovo cliente"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-bold">Nome / Ragione sociale *</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  placeholder="es. ROSSI SRL"
                  className="h-11"
                  data-testid="cliente-nome-input"
                  autoFocus
                />
                <Button
                  type="button"
                  onClick={research}
                  disabled={researching || !form.nome.trim()}
                  data-testid="cliente-research-button"
                  className="h-11 bg-emerald-600 hover:bg-emerald-700 whitespace-nowrap"
                >
                  {researching ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : (
                    <Sparkles className="h-4 w-4 mr-1" />
                  )}
                  Cerca dati online
                </Button>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">
                L&apos;AI cerca su internet partita IVA, indirizzo e altri dati.
                Compili solo il nome, poi conferma quello che propone.
              </p>
            </div>

            {/* Iter 31t/31u — Banner conferma sito ufficiale: fino a 3 candidati */}
            {siteCandidate && (
              <div
                className="border-2 border-purple-300 bg-purple-50 rounded-md p-3 space-y-2"
                data-testid="site-candidate-banner"
              >
                <div className="text-xs uppercase font-bold tracking-wide text-purple-700">
                  Sito ufficiale — scegli il candidato giusto
                </div>
                <div className="space-y-2">
                  {(siteCandidate.candidates || []).map((c, idx) => (
                    <div
                      key={c.sito_web || `candidate-${idx}`}
                      className="rounded-md border border-purple-200 bg-white p-2"
                      data-testid={`site-candidate-${idx}`}
                    >
                      <a
                        href={
                          c.sito_web.startsWith("http")
                            ? c.sito_web
                            : `https://${c.sito_web}`
                        }
                        target="_blank"
                        rel="noreferrer"
                        className="block font-mono text-sm text-blue-700 hover:underline break-all"
                      >
                        {c.sito_web}
                      </a>
                      <div className="text-[11px] text-slate-700 mt-1 flex flex-wrap items-center gap-x-1.5">
                        {c.reachable ? (
                          <span
                            className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 px-1.5 py-0.5 text-[10px] font-semibold"
                            data-testid={`site-candidate-${idx}-reachable`}
                            title="Verificato: il dominio risponde"
                          >
                            ● online
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center rounded-full bg-rose-100 text-rose-700 px-1.5 py-0.5 text-[10px] font-semibold"
                            data-testid={`site-candidate-${idx}-unreachable`}
                            title="Il dominio non risponde — potrebbe non esistere"
                          >
                            ✕ non raggiungibile
                          </span>
                        )}
                        {c.settore ? <span>{c.settore}</span> : null}
                        {c.paese ? <span>· {c.paese}</span> : null}
                        <span>·</span>
                        <span>
                          confidenza{" "}
                          <strong
                            className={
                              c.confidenza === "alta"
                                ? "text-emerald-700"
                                : c.confidenza === "bassa"
                                ? "text-amber-700"
                                : "text-purple-700"
                            }
                          >
                            {c.confidenza}
                          </strong>
                        </span>
                        {c.note ? (
                          <span className="opacity-70 italic">· {c.note}</span>
                        ) : null}
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        className="mt-1.5 bg-emerald-600 hover:bg-emerald-700"
                        disabled={researching}
                        onClick={() => researchFromSite(c)}
                        data-testid={`site-candidate-${idx}-confirm`}
                      >
                        {researching && busySiteUrl === (c.sito_web || "") ? (
                          <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        ) : (
                          <Sparkles className="h-3.5 w-3.5 mr-1" />
                        )}
                        Usa questo sito
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      const name = siteCandidate.nome;
                      setSiteCandidate(null);
                      setResearching(true);
                      await researchGeneric(name);
                      setResearching(false);
                    }}
                    disabled={researching}
                    data-testid="site-candidate-reject"
                  >
                    Nessuno di questi · cerca senza sito
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setSiteCandidate(null)}
                    disabled={researching}
                  >
                    Annulla
                  </Button>
                </div>
              </div>
            )}

            {proposedHint?.duplicate && (
              <div
                className="border border-amber-300 bg-amber-50 rounded-md p-3 text-sm"
                data-testid="cliente-duplicate-warning"
              >
                <div className="font-bold text-amber-800 flex items-center gap-1">
                  <ShieldAlert className="h-4 w-4" />
                  Cliente simile già presente
                </div>
                <div className="text-xs text-amber-900 mt-1">
                  <span className="font-bold">{proposedHint.duplicate.nome}</span>
                  {proposedHint.duplicate.partita_iva && (
                    <span> · P.IVA {proposedHint.duplicate.partita_iva}</span>
                  )}
                  . Verifica se è lo stesso cliente prima di salvare.
                </div>
              </div>
            )}

            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-7">
                <Label className="font-bold text-xs">
                  Partita IVA <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={form.partita_iva}
                  onChange={(e) => setForm({ ...form, partita_iva: e.target.value })}
                  placeholder="IT 11 cifre o estera"
                  className="h-11 mt-1 font-mono"
                  data-testid="cliente-piva-input"
                />
              </div>
              <div className="col-span-5">
                <Label className="font-bold text-xs">Codice fiscale</Label>
                <Input
                  value={form.codice_fiscale}
                  onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value })}
                  className="h-11 mt-1 font-mono"
                  data-testid="cliente-cf-input"
                />
              </div>
            </div>

            {/* Iter 31w: lista stabilimenti (codice + nome opzionale) */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-bold text-sm text-blue-900">
                  Stabilimenti / codici EG-XX{" "}
                  <span className="text-red-500">*</span>
                </Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs"
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      stabilimenti: [
                        ...(f.stabilimenti || []),
                        { codice: "", nome: "" },
                      ],
                    }))
                  }
                  data-testid="cliente-add-stabilimento"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Aggiungi stabilimento
                </Button>
              </div>
              <p className="text-[11px] text-slate-600 mb-2">
                Un cliente può avere N stabilimenti, ognuno col proprio codice
                a 2 cifre. Esempio: <em>Bormioli Luigi SpA</em> = stabilimento
                Parma (codice 90) + stabilimento Abbiategrasso (codice 91).
                Codici 01-98 unici. Codice 99 = jolly per lavorazioni rare.
              </p>
              <div className="space-y-1.5">
                {(form.stabilimenti || []).length === 0 && (
                  <div className="text-xs italic text-red-600 py-2 text-center">
                    Nessuno stabilimento: obbligatorio aggiungerne almeno uno
                    (codice EG-XX). Clicca &quot;Aggiungi&quot;.
                  </div>
                )}
                {(form.stabilimenti || []).map((st, idx) => (
                  <div
                    key={idx}
                    className="flex gap-2 items-center"
                    data-testid={`stabilimento-row-${idx}`}
                  >
                    <Input
                      value={st.codice}
                      onChange={(e) => {
                        const v = (e.target.value || "")
                          .replace(/[^0-9]/g, "")
                          .slice(0, 2);
                        setForm((f) => {
                          const next = [...(f.stabilimenti || [])];
                          next[idx] = { ...next[idx], codice: v };
                          return { ...f, stabilimenti: next };
                        });
                      }}
                      onBlur={(e) => {
                        const v = (e.target.value || "").replace(/[^0-9]/g, "");
                        if (v && v.length === 1) {
                          setForm((f) => {
                            const next = [...(f.stabilimenti || [])];
                            next[idx] = { ...next[idx], codice: "0" + v };
                            return { ...f, stabilimenti: next };
                          });
                        }
                      }}
                      placeholder="07"
                      className="h-10 w-20 font-mono text-center font-bold text-base"
                      data-testid={`stabilimento-codice-${idx}`}
                      maxLength={2}
                    />
                    <span className="text-slate-400 font-mono text-sm">
                      EG-{st.codice || "XX"}-…
                    </span>
                    <Input
                      value={st.nome}
                      onChange={(e) =>
                        setForm((f) => {
                          const next = [...(f.stabilimenti || [])];
                          next[idx] = { ...next[idx], nome: e.target.value };
                          return { ...f, stabilimenti: next };
                        })
                      }
                      placeholder="Nome stabilimento (es. Parma) — opzionale"
                      className="h-10 flex-1"
                      data-testid={`stabilimento-nome-${idx}`}
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      className="h-10 w-10 text-red-600 border-red-200 hover:bg-red-50 shrink-0"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          stabilimenti: (f.stabilimenti || []).filter(
                            (_, i) => i !== idx
                          ),
                        }))
                      }
                      data-testid={`stabilimento-remove-${idx}`}
                      title="Rimuovi stabilimento"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="font-bold text-xs">Indirizzo</Label>
              <Input
                value={form.indirizzo}
                onChange={(e) => setForm({ ...form, indirizzo: e.target.value })}
                placeholder="Via, numero civico"
                className="h-11 mt-1"
                data-testid="cliente-indirizzo-input"
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label className="font-bold text-xs">Città</Label>
                <Input
                  value={form.citta}
                  onChange={(e) => setForm({ ...form, citta: e.target.value })}
                  className="h-11 mt-1"
                  data-testid="cliente-citta-input"
                />
              </div>
              <div>
                <Label className="font-bold text-xs">CAP</Label>
                <Input
                  value={form.cap}
                  onChange={(e) => setForm({ ...form, cap: e.target.value })}
                  className="h-11 mt-1"
                  data-testid="cliente-cap-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="font-bold text-xs">Provincia</Label>
                <Input
                  value={form.provincia}
                  onChange={(e) =>
                    setForm({ ...form, provincia: e.target.value.toUpperCase() })
                  }
                  placeholder="MI"
                  maxLength={3}
                  className="h-11 mt-1"
                  data-testid="cliente-provincia-input"
                />
              </div>
              <div>
                <Label className="font-bold text-xs">Telefono</Label>
                <Input
                  value={form.telefono}
                  onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                  className="h-11 mt-1"
                  data-testid="cliente-telefono-input"
                />
              </div>
              <div>
                <Label className="font-bold text-xs">Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="h-11 mt-1"
                  data-testid="cliente-email-input"
                />
              </div>
            </div>

            <div>
              <Label className="font-bold text-xs">Sito web</Label>
              <Input
                value={form.sito_web}
                onChange={(e) => setForm({ ...form, sito_web: e.target.value })}
                placeholder="https://"
                className="h-11 mt-1"
                data-testid="cliente-sito-input"
              />
            </div>

            <div>
              <Label className="font-bold text-xs">Settore / Attività</Label>
              <Input
                value={form.settore}
                onChange={(e) => setForm({ ...form, settore: e.target.value })}
                placeholder="es. Produzione vetro industriale"
                className="h-11 mt-1"
                data-testid="cliente-settore-input"
              />
            </div>

            <div>
              <Label className="font-bold text-xs">Note</Label>
              <Input
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="h-11 mt-1"
                data-testid="cliente-note-input"
              />
            </div>

            {/* Iter 30t: ultime 3 commesse di questo cliente — clic apre Lista filtrata */}
            {editingId && recentCommesse.length > 0 && (
              <div
                className="border-t pt-3 mt-1"
                data-testid="cliente-recent-commesse"
              >
                <Label className="font-bold text-xs uppercase tracking-wide text-slate-600">
                  Ultime commesse di questo cliente
                </Label>
                <div className="mt-2 space-y-1.5">
                  {recentCommesse.map((cm) => {
                    const last = cm.last_activity
                      ? new Date(cm.last_activity).toLocaleDateString("it-IT", {
                          day: "2-digit", month: "2-digit", year: "numeric",
                        })
                      : "—";
                    return (
                      <a
                        key={cm.id}
                        href={`/lista?commessa=${encodeURIComponent(cm.codice)}`}
                        target="_blank"
                        rel="noreferrer noopener"
                        data-testid={`cliente-recent-commessa-${cm.id}`}
                        className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-slate-200 hover:border-[#0044FF] hover:bg-blue-50 transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-mono font-bold text-sm">
                            {cm.codice}
                          </div>
                          <div className="text-[11px] text-slate-500 truncate">
                            {cm.descrizione || "—"}
                          </div>
                        </div>
                        <div className="text-[11px] text-slate-500 shrink-0 inline-flex items-center gap-1">
                          ultima attività<span className="font-bold">{last}</span>
                          <Globe className="h-3 w-3 ml-1 text-[#0044FF]" />
                        </div>
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={save}
              data-testid="save-cliente-button"
              className="bg-[#0044FF] hover:bg-[#0033CC]"
            >
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 30w: alert clienti orfani all'ingresso della pagina */}
      <AlertDialog
        open={orfaniOpen}
        onOpenChange={(o) => !o && setOrfaniOpen(false)}
      >
        <AlertDialogContent className="max-w-2xl" data-testid="clienti-orfani-alert">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              {orfani.length} clienti senza commesse
            </AlertDialogTitle>
            <AlertDialogDescription>
              Questi clienti non sono collegati a nessuna commessa attiva.
              Seleziona quelli che vuoi spostare nel cestino (recuperabili 30
              giorni). I non selezionati restano in anagrafica.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-80 overflow-y-auto border rounded-md divide-y">
            {orfani.map((c) => {
              const checked = orfaniSelected.has(c.id);
              return (
                <label
                  key={c.id}
                  data-testid={`orfano-row-${c.id}`}
                  className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 ${
                    checked ? "bg-amber-50" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleOrfano(c.id)}
                    className="h-4 w-4 accent-amber-600"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm">{c.nome}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {c.partita_iva ? `P.IVA ${c.partita_iva}` : "P.IVA mancante"}
                      {c.citta && <span> · {c.citta}</span>}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
          <div className="text-xs text-slate-500 flex items-center justify-between mt-2">
            <button
              type="button"
              onClick={() =>
                setOrfaniSelected(
                  orfaniSelected.size === orfani.length
                    ? new Set()
                    : new Set(orfani.map((c) => c.id))
                )
              }
              className="underline hover:text-slate-700"
              data-testid="orfani-toggle-all"
            >
              {orfaniSelected.size === orfani.length
                ? "Deseleziona tutto"
                : "Seleziona tutto"}
            </button>
            <span>{orfaniSelected.size} selezionati</span>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="orfani-cancel">
              Tienili tutti
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="orfani-trash"
              className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
              disabled={orfaniBusy || orfaniSelected.size === 0}
              onClick={trashOrfani}
            >
              {orfaniBusy
                ? "In corso…"
                : `Sposta nel cestino (${orfaniSelected.size})`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
